/**
 * Environment Variables Debug Utility
 * 
 * This module provides functions to check if the required environment variables
 * are properly loaded and helps diagnose deployment issues.
 */

// Function to check Firebase environment variables
export function checkFirebaseEnvironment(): { 
  success: boolean;
  missing: string[];
  values: Record<string, string>;
} {
  const requiredVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_APP_ID',
    'VITE_FIREBASE_PROJECT_ID'
  ];
  
  const missing: string[] = [];
  const values: Record<string, string> = {};
  
  requiredVars.forEach(varName => {
    const value = import.meta.env[varName];
    
    if (!value) {
      missing.push(varName);
    } else {
      // Store the value, but mask sensitive data for display
      if (varName === 'VITE_FIREBASE_API_KEY') {
        const maskedKey = value.substring(0, 5) + '****' + value.substring(value.length - 5);
        values[varName] = maskedKey;
      } else {
        values[varName] = value;
      }
    }
  });
  
  return {
    success: missing.length === 0,
    missing,
    values
  };
}

// Function to check if we're in a Replit environment
export function isReplitEnvironment(): boolean {
  // Check for Replit-specific environment features
  const isReplit = typeof window !== 'undefined' && (
    window.location.hostname.includes('replit.dev') ||
    window.location.hostname.includes('replit.app') ||
    window.location.hostname.includes('repl.co')
  );
  
  return isReplit;
}

// Get deployment environment information
export function getEnvironmentInfo(): Record<string, any> {
  const info: Record<string, any> = {
    environment: process.env.NODE_ENV || import.meta.env.MODE,
    isReplit: isReplitEnvironment(),
    hasFirebase: checkFirebaseEnvironment().success,
    domain: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
    buildTime: import.meta.env.VITE_BUILD_TIME || 'not set',
    viteDefined: typeof import.meta.env !== 'undefined',
  };
  
  return info;
}

// Debug log for deployment 
export function logDeploymentInfo(): void {
  const envInfo = getEnvironmentInfo();
  const firebaseCheck = checkFirebaseEnvironment();
  
  console.log('=== DEPLOYMENT ENVIRONMENT INFO ===');
  console.log(JSON.stringify(envInfo, null, 2));
  
  if (!firebaseCheck.success) {
    console.error('⚠️ Missing Firebase environment variables:', firebaseCheck.missing);
    
    // Add domain-specific debugging info
    const domain = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
    
    if (domain === 'staymidle.com') {
      console.error('⚠️ staymidle.com deployment issue detected: Missing Firebase environment variables');
      console.log('Using hardcoded fallback values for Firebase configuration: apiKey, projectId, appId');
      
      // Set values directly in localStorage to help on reload
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('staymiddle_fallback_firebase_api_key', 'AIzaSyBiFd8SoSzG_ceZ48yKM8uSaPNAK2CrNHs');
          localStorage.setItem('staymiddle_fallback_firebase_project_id', 'staymiddle-com');
          localStorage.setItem('staymiddle_fallback_firebase_app_id', '1:289792559879:web:1f2efddb14ee0f5ee093a2');
        } catch (err) {
          console.warn('Unable to set localStorage fallbacks:', err);
        }
      }
    }
  } else {
    console.log('✅ Firebase environment configured correctly');
    console.log('Firebase config:', firebaseCheck.values);
  }
  
  // Add environment variables status for debugging
  console.log('All environment variables:', Object.keys(import.meta.env));
}